import { MASK_MARGIN_PX } from '@/infrastructure/engine2d/engine2dConstants';
import type { Canvas } from 'fabric';

// buildMaskPath draws with a stroke, so the mask visually extends
// past MASK_MARGIN_PX on both sides. The empirical factor 3 crops
// that stroke back out — kept as a named constant so the intent is
// readable even without knowing the mask geometry.
const MASK_STROKE_CROP_FACTOR = 3;

type TCompositeParams = {
    backgroundColor: string;
    clonedFabricCanvas: Canvas;
    imageRenderHeight: number;
    imageRenderWidth: number;
};

// Rescale the cloned fabric canvas into a fixed print-size raster:
// remap its viewport transform so the working zone maps 1:1 onto the
// target resolution, crop out the mask stroke, then re-draw onto a
// canvas of the exact target dimensions with the optional opaque
// background painted behind.
function compositeExportCanvas({
    backgroundColor,
    clonedFabricCanvas,
    imageRenderHeight,
    imageRenderWidth,
}: TCompositeParams): HTMLCanvasElement {
    const ratioX = clonedFabricCanvas.width / imageRenderWidth;
    const ratioY = clonedFabricCanvas.height / imageRenderHeight;
    clonedFabricCanvas.setViewportTransform([ratioX, 0, 0, ratioY, 0, 0]);
    clonedFabricCanvas.renderAll();

    const cropMargin = MASK_MARGIN_PX * MASK_STROKE_CROP_FACTOR;
    const croppedCanvas = clonedFabricCanvas.toCanvasElement(1, {
        height: imageRenderHeight - cropMargin,
        left: MASK_MARGIN_PX,
        top: MASK_MARGIN_PX,
        width: imageRenderWidth - cropMargin,
    });

    const outputCanvas = document.createElement('canvas');
    outputCanvas.width = imageRenderWidth;
    outputCanvas.height = imageRenderHeight;
    const ctx = outputCanvas.getContext('2d');
    if (!ctx) {
        throw new Error('Cannot initialize the 2D context of the export canvas');
    }

    if (backgroundColor && backgroundColor !== 'transparent') {
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, outputCanvas.width, outputCanvas.height);
    }
    ctx.drawImage(croppedCanvas, 0, 0, croppedCanvas.width, croppedCanvas.height, 0, 0, outputCanvas.width, outputCanvas.height);
    return outputCanvas;
}

export { compositeExportCanvas };
